import type { OpenCourseRun, StudentCourse } from "../Lib/courseApi";
import { courseLifecycle, courseProgressPercent, nextCourseAction, type CourseNextAction } from "./course";

export type JourneyCategory = "knowledge" | "experience" | "evaluation";
export type JourneyStatus = "red" | "orange" | "green";
export type JourneyItemState = "active" | "completed";

export type InterviewActivity = {
    id: string;
    tier?: "SERVICE" | "PRODUCT" | "MAANG";
    status?: "pending" | "slot_requested" | "awaiting_payment" | "payment_failed" | "payment_review" | "confirmed" | "rejected" | "completed";
    targetRole?: string;
    interviewType?: string;
    preferredDate?: string;
    scheduledDate?: string;
};

export type InterviewFeedback = {
    id: string;
    interviewType?: string;
    role?: string;
    summary?: string;
    overallScore?: number | string;
    score?: number | string;
    rating?: number | string;
};

export type JourneyItem = {
    id: string;
    name: string;
    state: JourneyItemState;
    progress: number;
    programId?: string;
    trackLabel?: string;
    tier?: number;
};

export type JourneyStage = {
    category: JourneyCategory;
    label: string;
    status: JourneyStatus;
    items: JourneyItem[];
    emptyText: string;
    cta: string;
    comingSoon?: boolean;
};

export function journeyCategoryForCourseType(courseType?: string): JourneyCategory | undefined {
    const normalized = String(courseType || "COURSE").trim().toUpperCase();
    if (normalized === "INTERNSHIP") return "experience";
    if (normalized === "WORKSHOP" || normalized === "COURSE") return "knowledge";
    if (normalized === "MOCK_INTERVIEW") return "evaluation";
    return undefined;
}

function statusFor(items: JourneyItem[]): JourneyStatus {
    if (items.some((item) => item.state === "completed")) return "green";
    if (items.some((item) => item.state === "active")) return "orange";
    return "red";
}

function courseItem(item: StudentCourse): JourneyItem | undefined {
    if (item.course.courseStatus === "cancelled" || item.course.courseStatus === "draft") return undefined;
    const state = item.course.courseStatus === "completed" ? "completed" : "active";
    return {
        id: item.course.id,
        programId: item.course.programId,
        name: item.course.courseTitle,
        state,
        progress: state === "completed" ? 100 : courseProgressPercent(item.course, item.submissions),
        trackLabel: item.course.programTrackLabel,
        tier: item.course.programTier,
    };
}

function ctaFor(category: JourneyCategory, status: JourneyStatus) {
    if (category === "knowledge") return status === "red" ? "Start learning" : status === "orange" ? "Continue Workshop" : "Learn a new skill";
    if (category === "experience") return status === "red" ? "Find an internship" : status === "orange" ? "Continue Internship" : "Explore another internship";
    return status === "red" ? "Book a mock interview" : status === "orange" ? "View Mock Interview" : "Book another mock interview";
}

export function buildCareerJourney(courses: StudentCourse[], interviews: InterviewActivity[], feedback: InterviewFeedback[], mockInterviewsEnabled: boolean): JourneyStage[] {
    const courseItems = courses.map((course) => ({ category: journeyCategoryForCourseType(course.course.courseType), item: courseItem(course) }))
        .filter((entry): entry is { category: JourneyCategory; item: JourneyItem } => Boolean(entry.category && entry.item));
    const stage = (category: JourneyCategory, label: string, emptyText: string): JourneyStage => {
        const items = courseItems.filter((entry) => entry.category === category).map((entry) => entry.item)
            .sort((a, b) => a.state === b.state ? a.name.localeCompare(b.name) : a.state === "active" ? -1 : 1);
        const status = statusFor(items);
        return { category, label, status, items, emptyText, cta: ctaFor(category, status) };
    };
    const knowledge = stage("knowledge", "Knowledge", "No workshops or courses started yet.");
    const experience = stage("experience", "Experience", "No internships started yet.");
    if (!mockInterviewsEnabled) {
        return [knowledge, experience, { category: "evaluation", label: "Evaluation", status: "red", items: [], emptyText: "Mock interviews are coming soon.", cta: "Coming soon", comingSoon: true }];
    }
    const evaluationItems: JourneyItem[] = interviews.filter((item) => ["pending", "slot_requested", "awaiting_payment", "confirmed", "completed"].includes(item.status || "")).map((item) => ({
        id: item.id,
        name: item.targetRole || item.interviewType || "Mock Interview",
        state: item.status === "completed" ? "completed" : "active",
        progress: item.status === "completed" ? 100 : item.status === "confirmed" ? 75 : item.status === "awaiting_payment" ? 50 : 25,
    }));
    feedback.filter((item) => !evaluationItems.some((entry) => entry.id === item.id)).forEach((item) => evaluationItems.push({ id: `feedback-${item.id}`, name: item.interviewType || item.role || "Mock Interview", state: "completed", progress: 100 }));
    const evaluationStatus = statusFor(evaluationItems);
    return [knowledge, experience, {
        category: "evaluation", label: "Evaluation", status: evaluationStatus,
        items: evaluationItems.sort((a, b) => a.state === b.state ? a.name.localeCompare(b.name) : a.state === "active" ? -1 : 1),
        emptyText: "No mock interviews taken yet.", cta: ctaFor("evaluation", evaluationStatus),
    }];
}

function availableRun(run: OpenCourseRun, enrolledRunIds: Set<string>) {
    return !run.alreadyEnrolled
        && !enrolledRunIds.has(run.id)
        && (run.seatsRemaining === undefined || run.seatsRemaining > 0)
        && new Date(run.startAt).getTime() > Date.now()
        && Boolean(run.programTrackKey && run.programTrackLabel && run.programTier);
}

export function selectRecommendedProgram(openRuns: OpenCourseRun[], courses: StudentCourse[]): OpenCourseRun | undefined {
    const enrolledRunIds = new Set(courses.map((item) => item.course.id));
    const maxTierByTrack = new Map<string, number>();
    courses.filter((item) => item.course.courseStatus !== "cancelled" && item.course.programTrackKey && item.course.programTier).forEach((item) => {
        const key = item.course.programTrackKey!;
        maxTierByTrack.set(key, Math.max(maxTierByTrack.get(key) || 0, item.course.programTier || 0));
    });
    const candidates = openRuns.filter((run) => availableRun(run, enrolledRunIds));
    const progression = candidates.filter((run) => {
        const currentTier = maxTierByTrack.get(run.programTrackKey || "");
        return currentTier !== undefined && Number(run.programTier) > currentTier;
    }).sort((a, b) => {
        const aGap = Number(a.programTier) - (maxTierByTrack.get(a.programTrackKey || "") || 0);
        const bGap = Number(b.programTier) - (maxTierByTrack.get(b.programTrackKey || "") || 0);
        return aGap - bGap || a.startAt.localeCompare(b.startAt);
    });
    if (progression[0]) return progression[0];
    return candidates.filter((run) => run.programTier === 1 && !maxTierByTrack.has(run.programTrackKey || ""))
        .sort((a, b) => a.startAt.localeCompare(b.startAt))[0];
}

export type StudentCourseAction = { item: StudentCourse; action: CourseNextAction };

export function activeStudentCourses(courses: StudentCourse[]) {
    return courses.filter((item) => item.course.courseStatus === "published");
}

export function currentStudentCourses(courses: StudentCourse[], now = new Date()) {
    return courses.filter((item) => courseLifecycle(item.course, now) === "active").sort((a, b) => a.course.endAt.localeCompare(b.course.endAt));
}

export function upcomingStudentCourses(courses: StudentCourse[], now = new Date()) {
    return courses.filter((item) => courseLifecycle(item.course, now) === "scheduled").sort((a, b) => a.course.startAt.localeCompare(b.course.startAt));
}

export function completedStudentCourses(courses: StudentCourse[]) {
    return courses.filter((item) => item.course.courseStatus === "completed");
}

export function upcomingStudentActions(courses: StudentCourse[]): StudentCourseAction[] {
    return currentStudentCourses(courses).map((item) => ({ item, action: nextCourseAction(item.course, item.submissions) }))
        .filter((entry): entry is StudentCourseAction => Boolean(entry.action))
        .sort((a, b) => (a.action.at || "").localeCompare(b.action.at || ""));
}

export function selectPrimaryActiveCourse(courses: StudentCourse[]) {
    const actions = new Map(upcomingStudentActions(courses).map((entry) => [entry.item.course.id, entry.action]));
    return currentStudentCourses(courses).sort((a, b) => {
        const aAction = actions.get(a.course.id);
        const bAction = actions.get(b.course.id);
        if (aAction && !bAction) return -1;
        if (!aAction && bAction) return 1;
        return (aAction?.at || a.course.endAt).localeCompare(bAction?.at || b.course.endAt);
    })[0];
}
